const NEW_LINE = "\r\n";

const isForcedLogoutExemptUrl = (url) => {
  return !!url && (url.includes("/auth/login") || /\/externalBulkOrders\//.test(url));
};

// Backend PUBLIC_ACCESS endpoints - a stale token from the shared `.obiado.pl` cookie must never reach these
const PUBLIC_PRE_AUTH_URL_PATTERNS = [
  /\/api\/frontend\/auth\/(?!currentUser)/,
  /\/api\/auth\/register\/modes(\?|$)/,
  /\/api\/frontend\/common\/settings\/public(\?|$)/,
  /\/api\/frontend\/districts\/options(\?|$)/,
];

const isPublicPreAuthUrl = (url) => {
  return !!url && PUBLIC_PRE_AUTH_URL_PATTERNS.some((pattern) => pattern.test(url));
};

const stripStaleAuthorization = (config) => {
  if (!isPublicPreAuthUrl(config.url)) {
    return config;
  }

  delete config.headers?.Authorization;

  if (config.headers?.common) {
    delete config.headers.common.Authorization;
  }

  return config;
};

const handleSessionExpired = (store, i18n) => {
  // auth/logout already resets every module with user-scoped state (balance, active
  // orders amount, ...), both in memory and in localStorage - no need to do it here too.
  store.dispatch("auth/logout");

  store.commit("notifications/showNotification", {
    type: "error",
    text: i18n.t("Please login again, to continue."),
  });
};

const handleNetworkError = (store, i18n) => {
  store.commit("notifications/showNotification", {
    type: "error",
    text: i18n.t("Could not connect to the server. Check your internet connection and try again."),
  });
};

// Translates an already-translated message coming from the backend. i18n.t() is meant for
// translation *keys*, not arbitrary sentences, but the backend's legacy `message` field is a
// full Polish/English/Ukrainian sentence - passing it through here only "works" because vue-i18n
// echoes back whatever it doesn't recognize as a key. A sentence containing `|`, `{`, `}` or `@:`
// is valid vue-i18n message syntax though, and would get parsed (pluralization, interpolation,
// linked messages) instead of just failing a lookup - so this is wrapped defensively.
const translateBackendMessage = (i18n, message) => {
  try {
    return i18n.t(message);
  } catch {
    return message;
  }
};

const buildErrorText = (data, i18n, fallbackText) => {
  // A falsy message covers both a missing `message` field and an empty string - the latter
  // happens when a TranslatableException has no messageKey set, so trans('') returns ''.
  if (!data?.message) {
    // No human-readable message, but the machine-readable key survived - show it rather than
    // a fully generic message, so the failure is at least identifiable/searchable. This is not
    // a real translation lookup (the frontend catalogue has no "exceptions.*" keys yet), so
    // i18n.t() just echoes the key back today; it becomes a real translation for free once/if
    // those keys are ever added to the frontend catalogues.
    if (data?.key) {
      return i18n.t(data.key);
    }

    return fallbackText;
  }

  let message = translateBackendMessage(i18n, data.message);

  if (data.errors) {
    message += NEW_LINE + NEW_LINE;

    for (let key in data.errors) {
      message += "• " + data.errors[key] + NEW_LINE;
    }
  }

  return message;
};

const handleGenericError = async (err, store, i18n, fallbackText) => {
  let data = err?.response?.data;

  if (data?.constructor?.name === "Blob") {
    try {
      data = JSON.parse(await data.text());
    } catch {
      // Not a JSON body (e.g. an HTML error page from an unhandled 500) - fall through to the
      // generic message below instead of losing the notification entirely.
      data = null;
    }
  }

  store.commit("notifications/showNotification", {
    type: "error",
    text: buildErrorText(data, i18n, fallbackText),
  });
};

// For call sites that suppress the automatic interceptor toast (via `suppressErrorNotification`)
// because they need to branch on the error first, but still want the same toast as a fallback
// for whichever error cases they don't handle themselves.
export const showApiErrorNotification = async (err, fallbackText) => {
  const i18n = (await import("@/lib/i18n")).default;
  const store = (await import("@/lib/store")).default;

  if (err?.response?.status === undefined) {
    handleNetworkError(store, i18n);

    return;
  }

  await handleGenericError(
    err,
    store,
    i18n,
    fallbackText ?? i18n.t("Error occurred while processing your request."),
  );
};

export const applyAxiosInterceptor = (axios) => {
  axios.interceptors.request.use(stripStaleAuthorization);

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (err) => {
      // Dynamic import to avoid a circular dependency: i18n uses axios internally
      // (to fetch the user's saved language), and this file configures axios's interceptors.
      const i18n = (await import("@/lib/i18n")).default;
      const store = (await import("@/lib/store")).default;

      const status = err?.response?.status;
      const isAuthenticated = store.getters["auth/isAuthenticated"];

      // Only kick the user back to login if a session that was actually active just expired.
      // A 401 from an anonymous request (e.g. on a public page) is a plain error, not a logout.
      if (status === 401 && isAuthenticated && !isForcedLogoutExemptUrl(err?.config?.url)) {
        handleSessionExpired(store, i18n);
      } else if (!err?.config?.suppressErrorNotification) {
        if (status === undefined) {
          // No `err.response` at all: request never reached the server (network error, CORS,
          // timeout, aborted). There is no backend body to read a message from.
          handleNetworkError(store, i18n);
        } else if (status === 403) {
          // Prefer the server's specific message over the generic "Forbidden." - the backend
          // already translates it to the user's locale (see ForbiddenException::$messageKey).
          await handleGenericError(err, store, i18n, i18n.t("Forbidden."));
        } else if (status > 399) {
          await handleGenericError(
            err,
            store,
            i18n,
            i18n.t("Error occurred while processing your request."),
          );
        }
      }

      return Promise.reject(err);
    },
  );
};
